import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { personDetailsFormInitialValues } from '@common/constants';
import { useGetPersonDetailsForm, useUpatePersonInfo } from '@common/slices';
import {
  IPersonDetailsDrawerInfo,
  IPersonDetailsDrawerProps
} from '@common/types';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import DateService from '@core/services/DateService';
import { getSections } from '@modules/salesforce/persons/person-details-page/personal-information-drawer/content';
import { toLookups } from '@modules/salesforce/persons/person-details-page/personal-information-drawer/utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

import { cloneDeep, isEmpty } from 'lodash';

const PersonDetailsDrawer: React.FC<IPersonDetailsDrawerProps> = ({
  personId,
  open,
  onClose,
  personDetailsInfo
}) => {
  const [booted, setBooted] = useState<boolean>(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [values, setValues] = useState<IPersonDetailsDrawerInfo>();
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sectionsForm, setSectionsForm] = useState<IDynamicFormWidgetSection[]>(
    getSections(values, lovs)
  );
  const { data } = useGetPersonDetailsForm();

  const [updatePersonInfo] = useUpatePersonInfo(personId);

  const loadLovList = async () => {
    const personEnums = toLookups(data);

    return personEnums;
  };

  const initialize = async () => {
    try {
      const lovData = await loadLovList();
      const newValues = cloneDeep(
        personDetailsInfo ? personDetailsInfo : personDetailsFormInitialValues
      );

      setLovs(lovData);
      setValues(newValues);
      setSectionsForm(getSections(newValues, lovData));
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, [data]);

  const onChange = async (
    fieldName: keyof IPersonDetailsDrawerInfo,
    value: unknown,
    sectionIndex: number,
    values: IPersonDetailsDrawerInfo
  ) => {
    const newSections = cloneDeep(sectionsForm);
    const newValues = cloneDeep(values) as any;
    newSections[sectionIndex].inputs[fieldName as string].value = value;
    newValues[fieldName] = value;
    setSectionsForm(newSections);
    setValues(newValues);
  };

  const submitForm = async (data: Record<string, any>) => {
    setSubmitButtonState('loading');
    setFormDisabled(true);

    try {
      const selectedType = data.type.toLowerCase();
      const isPerson = selectedType === 'individual';
      const isCompany = selectedType === 'company';

      const variablesMutation = {
        input: {
          id: personId,
          type: data.type,
          firstName: isPerson ? data.firstName : null,
          middleName: isPerson ? data.middleName : null,
          lastName: isPerson ? data.lastName : null,
          maidenName: isPerson ? data.maidenName : null,
          dateOfBirth:
            isPerson && data.dateOfBirth
              ? DateService.formatDateBackend(data.dateOfBirth)
              : null,
          title: isPerson ? data.title : null,
          companyName: isCompany ? data.companyName : null,
          initials: data.initials || null,
          nationality: data.nationality || null,
          managerName: isCompany ? data.managerName : null,
          email: data.email || null,
          phoneType: data.phoneType,
          phoneNumber: data.phoneNumber,
          relation: data.relation || null,
          relativeOf: data.relativeOf || null,
          smoker:
            isPerson && data?.smoker
              ? data?.smoker?.toLowerCase() === 'yes'
                ? true
                : false
              : null,
          weight: isPerson ? parseFloat(data.weight) : null,
          height: isPerson ? parseFloat(data.height) : null,
          dateOfDeath:
            isPerson && data.dateOfDeath
              ? DateService.formatDateBackend(data.dateOfDeath)
              : null,
          dateOfDeathNotified:
            isPerson && data.dateOfDeathNotified
              ? DateService.formatDateBackend(data.dateOfDeathNotified)
              : null,
          taxable: data.taxable === 'Yes' ? true : false,
          taxResidenceCountry: data.taxResidenceCountry || null,
          financialReference: data.financialReference || null,
          occupationClass: isPerson ? data.occupationClass : null,
          profession: isPerson ? data.profession : null,
          employer: isPerson ? data.employer : null,
          dateOfEmployment:
            isPerson && data.dateOfEmployment
              ? DateService.formatDateBackend(data.dateOfEmployment)
              : null,
          salary: isPerson ? parseFloat(data.salary) : null,
          dateOfSelfCertificateReceived:
            isPerson && data.dateOfSelfCertificateReceived
              ? DateService.formatDateBackend(
                  data.dateOfSelfCertificateReceived
                )
              : null,
          dateOfSelfCertificateValidated:
            isPerson && data.dateOfSelfCertificateValidated
              ? DateService.formatDateBackend(
                  data.dateOfSelfCertificateValidated
                )
              : null,
          category: data.category || null,
          dateOfKYC:
            isPerson && data.dateOfKYC
              ? DateService.formatDateBackend(data.dateOfKYC)
              : null,
          comments: data.comments || null,
          remarks: data.remarks || null,
          placeOfRegistration: isCompany ? data.registrationPlace : null,
          dateOfRegistration:
            isCompany && data.registrationDate
              ? DateService.formatDateBackend(data.registrationDate)
              : null,
          legalRepresentative: isCompany
            ? {
                fullName: data.legalRepresentativeFullName || null,
                dateOfBirth: data.legalRepresentativeDateOfBirth
                  ? DateService.formatDateBackend(
                      data.legalRepresentativeDateOfBirth
                    )
                  : null,
                nationality: data.legalRepresentativeNationality || null
              }
            : null
        }
      };

      const res = await updatePersonInfo(variablesMutation);

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {'Person successfully updated'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          setFormDisabled(false);
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        setFormDisabled(false);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (error) {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormDrawer
      title="Modify Personal Information"
      onClose={() => onClose()}
      isOpen={open}
      sections={sectionsForm}
      loading={!booted}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onChange={onChange}
    />
  );
};

export { PersonDetailsDrawer };
